import Panel from "./Panel.svelte";
import { Node, mergeAttributes } from "@tiptap/core";
import { SvelteNodeViewRenderer } from "svelte-tiptap";
import { v4 as uuidv4 } from "uuid";
import { tabBlocks } from "$components/editor/extensions/blocks/Tabs/tabs";
import { SvelteSSRRenderer } from "$components/editor/utils";
import { type MarkdownSerializerState } from "prosemirror-markdown";
import { type Node as PMNode } from "@tiptap/pm/model";

export interface Tab {
  id: string;
  title: string;
  blockId: string;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    tabsBlock: {
      /**
       * Add a new tab black
       */
      addTabBlock: () => ReturnType;
    };
  }
}

export default Node.create({
  name: "tabsBlock",
  group: "block",
  content: "tabNode+", // allow one or more tabNode only
  draggable: true,
  defining: true,

  addAttributes() {
    return {
      id: { default: uuidv4() },
      tabs: {
        default: [],
      },
    };
  },

  addCommands() {
    return {
      addTabBlock:
        () =>
        ({ commands }) => {
          const newTabBlockId = uuidv4();

          const defaultTabs = [
            { id: uuidv4(), title: "Tab 1" },
            { id: uuidv4(), title: "Tab 2" },
          ];

          const defaultTabsContent = defaultTabs.map((tab) => ({
            type: "tabNode",
            attrs: {
              id: tab.id,
              title: tab.title,
              blockId: newTabBlockId,
            },
            content: [
              {
                type: "paragraph",
              },
            ],
          }));

          tabBlocks.setActiveTab(newTabBlockId, defaultTabs[0].id, "right");

          return commands.insertContent([
            {
              type: this.name,
              attrs: { id: newTabBlockId, tabs: defaultTabs },
              content: defaultTabsContent,
            },
            { type: "paragraph" }, // add an empty paragraph under the tabs block
          ]);
        },
    };
  },

  addStorage() {
    return {
      markdown: {
        serialize: (state: MarkdownSerializerState, node: PMNode) => {
          // map tab IDs to titles from node.attrs.tabs
          const tabsMap = new Map(node.attrs.tabs.map((tab: Tab) => [tab.id, tab.title]));

          node.forEach((tabNode) => {
            if (tabNode.type.name === "tabNode") {
              const title = tabsMap.get(tabNode.attrs.id) || "Untitled Tab";
              state.write("--------\n");
              state.write(`#### ${title}\n`);
              state.write("--------\n\n");
              state.renderContent(tabNode);
              state.write("\n");
            }
          });

          // final format looks like this:
          // --------
          // #### Tab 1 title
          // --------
          //
          // Content of Tab 1
          //
          // --------
          // #### Tab 2 title
          // --------
          //
          // Content of Tab 2
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "tabs-panel",
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    return SvelteSSRRenderer("tabs-panel", Panel, {
      node,
      hasContent: true,
      attributes: mergeAttributes(node.attrs, HTMLAttributes),
    });
  },

  addNodeView() {
    return SvelteNodeViewRenderer(Panel);
  },
});
